<template>
  <BasePopup ref="popup" class="checklist-docs" title="Choose a book" compact>
    <ul class="checklist-docs__list">
      <li v-for="doc of docs" :key="doc.id" class="checklist-docs__doc-wrapper">
        <router-link
          class="checklist-docs__doc"
          :to="`/app/book/${doc.url_slug}/preview`"
        >
          <img
            class="checklist-docs__doc-cover"
            :src="doc.cover_image"
            width="54"
            height="72"
            alt=""
          />

          <div class="checklist-docs__doc-text">
            <h3 class="checklist-docs__doc-title">{{ doc.title }}</h3>
            <p class="checklist-docs__doc-subtitle">{{ doc.author }}</p>
          </div>
        </router-link>
      </li>
    </ul>

    <div class="checklist-docs__footer">
      <router-link class="checklist-docs__link" :to="{ name: 'books' }"
        >Browse the full library</router-link
      >
      <span class="iconfont iarrow"></span>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'

import { ChecklistView } from '@/models/checklist'
import type { ListItem } from '@/models/interfaces'

import BasePopup from '@/components/ui/BasePopup.vue'

defineProps<{
  checklist: ChecklistView
  docs: ListItem[]
}>()

const basePopup = useTemplateRef('popup')

async function show(): Promise<void> {
  basePopup.value?.show()
}

defineExpose({
  show,
})
</script>

<style lang="scss" scoped>
.checklist-docs {
  &__list {
    width: 100%;
    padding: 0;
  }

  &__doc-wrapper {
    border-bottom: 1px solid $c-border;

    .night-mode & {
      border-color: $c-dark-5;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__doc {
    display: flex;
    align-items: center;
    gap: 16px;
    width: calc(100% + 28px);
    padding: 14px;
    margin-left: -14px;
    border-radius: 12px;

    @include hover {
      background: $c-bg;

      .night-mode & {
        background: $c-dark-2;
      }

      .checklist-docs__doc-cover {
        transform: scale(1.1);
      }
    }
  }

  &__doc-cover {
    width: 54px;
    height: auto;
    border: 1px solid $c-border;
    border-radius: 8px;
    transition: all 0.2s;
  }

  &__doc-text {
    flex: 1;
  }

  &__doc-title {
    font-size: 16px;
    line-height: 22px;
    text-wrap: balance;
    color: $c-black-2;
    margin: 0;

    .night-mode & {
      color: $c-dark-4;
    }
  }

  &__doc-subtitle {
    font-size: 13px;
    line-height: 18px;
    color: $c-black-6;
    margin: 0;

    .night-mode & {
      color: $c-dark-8;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 16px;
    font-size: 14px;
    line-height: 18px;
    color: $c-black-3;

    .iconfont {
      display: block;
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 1;
      margin-top: -2px;
      transition: color 0.2s;
    }

    .night-mode & {
      color: $c-black-4;
    }
  }

  &__link {
    display: flex;
    align-items: center;
    color: inherit;

    @include hover {
      color: $c-black-2;

      + .iconfont {
        color: $c-black-2;
      }

      .night-mode &,
      .night-mode & + .iconfont {
        color: $c-dark-4;
      }
    }
  }
}
</style>
